<script lang="ts" setup>
import { ref, type VNodeChild } from 'vue';
import { useFileDialog, type UseFileDialogOptions } from '@vueuse/core';
import { IconUpload } from '@/icons';
import { isValidFileType, hasSlotContent } from '../../utils';
import { UiButton } from '../ui-button';

interface Props {
  accept?: string;
  maxFileSize?: number;
}

interface ValidationError {
  reason: 'size' | 'type';
}

const props = defineProps<Props>();

const emit = defineEmits<{
  selected: [file: File];
  validationFailed: [error: ValidationError];
}>();

defineSlots<{
  title?: () => VNodeChild;
  description?: () => VNodeChild;
  before?: () => VNodeChild;
  button?: (scope: { selectFile: (localOptions?: Partial<UseFileDialogOptions>) => void }) => VNodeChild;
  note?: () => VNodeChild;
  after?: () => VNodeChild;
}>();

function handleFileSelection(file: File) {
  if (props.maxFileSize && file.size > props.maxFileSize) {
    emit('validationFailed', { reason: 'size' });
    return;
  }

  if (props.accept && !isValidFileType(file, props.accept)) {
    emit('validationFailed', { reason: 'type' });
    return;
  }

  emit('selected', file);
}

const { onChange, open } = useFileDialog({
  accept: props.accept,
  directory: false,
  multiple: false,
  reset: true,
});

onChange((files) => {
  if (files) {
    handleFileSelection(files[0]);
  }
});

const dragActive = ref(false);

function handleDragEvent(event: DragEvent) {
  dragActive.value = false;

  if (event.dataTransfer) {
    handleFileSelection(event.dataTransfer.files[0]);
  }
}

// Expose functions for testing
defineExpose({
  handleFileSelection,
});
</script>

<template>
  <div
    :class="[$style.root, { [$style.dragActive]: dragActive }]"
    @drop.prevent="handleDragEvent"
    @dragover.prevent="dragActive = true"
    @dragleave.prevent="dragActive = false"
  >
    <div v-if="dragActive" :class="$style.dragHint">
      <!-- TODO: i18n -->
      Drag and drop the file here to upload.
    </div>
    <template v-else>
      <div :class="$style.before">
        <div v-if="hasSlotContent($slots.title)" :class="$style.title">
          <slot name="title" />
        </div>
        <div v-if="hasSlotContent($slots.description)" :class="$style.description">
          <slot name="description" />
        </div>
        <slot name="before" />
      </div>
      <div :class="$style.button">
        <slot name="button" v-bind="{ selectFile: open }">
          <UiButton size="l" @click="open()">
            <template #leadingVisual>
              <IconUpload aria-hidden="true" />
            </template>
            <!-- TODO: i18n -->
            Upload
          </UiButton>
        </slot>
      </div>
      <div :class="$style.bottom">
        <div :class="$style.note">
          <slot name="note" />
        </div>
        <div :class="$style.after">
          <slot name="after" />
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" module>
.root {
  display: grid;
  grid-template-rows: 1fr auto 1fr;
  min-height: 100%;
  border-radius: 20px;
  border: 2px dashed #d5d6d9;
  padding: 62px;
  box-sizing: border-box;
  position: relative;
  text-align: center;
}

.dragActive {
  border-radius: 20px;
  border-color: #d5b1dd;
  background: rgba(160, 120, 169, 0.04);
}

.dragHint {
  grid-row: 2;
  color: #a078a9;
  text-align: center;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  max-width: 180px;
  margin: 0 auto;
}

.before {
  grid-row: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 8px;
  align-items: center;
  margin-bottom: 32px;
}

.title {
  color: #010000;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: -0.32px;
}

.description {
  color: #978f91;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
}

.button {
  grid-row: 2;
  display: flex;
  justify-content: center;
}

.bottom {
  grid-row: 3;
  display: flex;
  flex-direction: column;
}

.note {
  color: #978f91;
  text-align: center;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  max-width: 200px;
  margin: 20px auto 0;
}

.after {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  flex: 1 1 auto;
  margin-top: 32px;
}
</style>
